import { Link } from "react-router-dom"
import {
  ArrowRight, BookOpen, Users, Award, Globe2, Microscope, Library,
  Trophy, Calendar as CalIcon, ChevronRight, Quote, Sparkles,
  GraduationCap, Building2, HandHeart, ShieldCheck, Megaphone,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { Button } from "../../components/ui/Button"
import { Card, CardContent } from "../../components/ui/Card"
import { Badge } from "../../components/ui/Badge"
import { SCHOOL } from "../../lib/constants"
import { usePublicNews, usePublicAnnouncements, usePublicCalendar, useAdmissionStatus } from "../../hooks/usePublic"
import { CountdownTimer } from "../../components/site/CountdownTimer"
import { formatDate } from "../../lib/constants"

const stats = [
  { label: "Students Enrolled", value: "12,500+", icon: Users },
  { label: "Academic Staff", value: "450+", icon: BookOpen },
  { label: "Programmes Offered", value: "35+", icon: Award },
  { label: "Years of Excellence", value: `${new Date().getFullYear() - 2014}+`, icon: Trophy },
]

const pillars = [
  { icon: Microscope, title: "Research-Driven", text: "Active faculty research feeding back into curriculum and pedagogy." },
  { icon: Library, title: "Modern Library", text: "Digital and print resources, E-journals and quiet study spaces." },
  { icon: Globe2, title: "Industry Partnerships", text: "Collaborations with schools, government and global institutions." },
  { icon: ShieldCheck, title: "Accredited Programmes", text: "All programmes fully accredited by NCCE and relevant bodies." },
]

export default function Home() {
  const { data: newsData } = usePublicNews(3)
  const { data: announceData } = usePublicAnnouncements()
  const { data: calendarData } = usePublicCalendar()
  const { data: admissionData } = useAdmissionStatus()

  const news = newsData?.news || []
  const announcements = announceData?.announcements || []
  const events = calendarData?.events || []
  const cycle = admissionData?.cycle
  const admissionOpen = Boolean(cycle?.application_open)

  return (
    <SiteShell>
      {/* HERO */}
      <section className="relative overflow-hidden bg-ink-950 dark:bg-ink-950 text-white">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2000&auto=format&fit=crop"
            alt="Campus"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-900/95 via-brand-950/90 to-ink-950/95" />
        </div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl py-24 md:py-36">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold-300 mb-5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15">
              <Sparkles className="w-3.5 h-3.5" />
              Established {SCHOOL.established} • Kano Ghari
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6">
              Shaping the Future of <span className="text-gradient-gold">Teacher Education</span> in Nigeria
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed mb-8">
              {SCHOOL.fullName} — a centre of academic excellence producing competent, ethical and
              globally competitive educators since {SCHOOL.established}.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={admissionOpen ? "/portal/register" : "/admission-requirements"}>
                <Button size="lg" className="bg-gold-500 hover:bg-gold-600 text-ink-950 font-semibold">
                  {admissionOpen ? "Apply for Admission" : "View Requirements"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/programs">
                <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/10">
                  Explore Programmes
                </Button>
              </Link>
            </div>

            {/* Admission status badge */}
            {cycle && (
              <div className="mt-8 inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-brand-700/30 border border-brand-400/30 backdrop-blur">
                <span className={`w-2.5 h-2.5 rounded-full ${admissionOpen ? "bg-green-400 animate-pulse" : "bg-red-400"}`} />
                <span className="text-sm font-medium">
                  Admission {admissionOpen ? "Open" : "Closed"}
                  {admissionOpen && cycle.application_close_at && <> • Closes {formatDate(cycle.application_close_at)}</>}
                </span>
              </div>
            )}

            {/* Countdown timer */}
            {admissionOpen && cycle?.application_close_at && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-gold-300 mb-2 font-semibold">Application Deadline</div>
                <CountdownTimer deadline={cycle.application_close_at} />
              </div>
            )}
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" className="w-full h-auto" preserveAspectRatio="none">
            <path fill="currentColor" className="text-ink-50 dark:text-ink-950" d="M0,32L60,37.3C120,43,240,53,360,53.3C480,53,600,43,720,37.3C840,32,960,32,1080,37.3C1200,43,1320,53,1380,58.7L1440,64L1440,80L0,80Z" />
          </svg>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-ink-50 dark:bg-ink-950 -mt-1">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 -mt-12 md:-mt-16 relative z-10">
            {stats.map(s => (
              <Card key={s.label} className="border-0 shadow-xl shadow-brand-900/5">
                <CardContent className="pt-6 text-center">
                  <s.icon className="w-8 h-8 mx-auto text-brand-700 dark:text-brand-400 mb-2" />
                  <div className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">{s.value}</div>
                  <div className="text-xs md:text-sm text-ink-500 dark:text-ink-400 mt-1 uppercase tracking-wider">{s.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ANNOUNCEMENTS + CALENDAR STRIP (admin-posted, shown on homepage) */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Announcements */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-xl font-bold text-ink-900 dark:text-ink-100 flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-brand-700 dark:text-brand-400" />
                    Announcements
                  </h2>
                </div>
                {announcements.length === 0 ? (
                  <p className="text-sm text-ink-500 dark:text-ink-400 text-center py-6">No announcements at the moment.</p>
                ) : (
                  <div className="space-y-3">
                    {announcements.slice(0, 4).map(a => (
                      <div key={a.id} className="border-l-4 pl-3 py-1" style={{
                        borderLeftColor: a.priority === "URGENT" ? "#dc2626" : a.priority === "IMPORTANT" ? "#f59e0b" : "#10b981"
                      }}>
                        <div className="flex items-center gap-2 mb-0.5">
                          <Badge variant={a.priority === "URGENT" ? "danger" : a.priority === "IMPORTANT" ? "warning" : "success"} className="text-[10px]">
                            {a.priority}
                          </Badge>
                          <span className="text-[11px] text-ink-400">{formatDate(a.created_at)}</span>
                        </div>
                        <h3 className="font-semibold text-sm text-ink-900 dark:text-ink-100">{a.title}</h3>
                        <p className="text-xs text-ink-600 dark:text-ink-400 line-clamp-2">{a.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Calendar events */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-xl font-bold text-ink-900 dark:text-ink-100 flex items-center gap-2">
                    <CalIcon className="w-5 h-5 text-gold-600 dark:text-gold-400" />
                    Upcoming Events
                  </h2>
                </div>
                {events.length === 0 ? (
                  <p className="text-sm text-ink-500 dark:text-ink-400 text-center py-6">No upcoming events.</p>
                ) : (
                  <div className="space-y-3">
                    {events.slice(0, 4).map(e => (
                      <div key={e.id} className="flex items-center gap-3 p-3 rounded-lg bg-ink-50 dark:bg-ink-800/50">
                        <div className="w-12 h-12 rounded-lg bg-brand-800 dark:bg-brand-700 text-white grid place-items-center shrink-0">
                          <div className="text-center">
                            <div className="text-[10px] uppercase opacity-80 leading-none">{new Date(e.start_date).toLocaleDateString("en-US", { month: "short" })}</div>
                            <div className="font-bold text-lg leading-tight">{new Date(e.start_date).getDate()}</div>
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-sm text-ink-900 dark:text-ink-100 truncate">{e.title}</h3>
                          {e.location && <p className="text-xs text-ink-500 dark:text-ink-400">{e.location}</p>}
                          <p className="text-[10px] text-ink-400 mt-0.5">{formatDate(e.start_date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-brand-100 dark:bg-brand-900">
                <img src="https://images.unsplash.com/photo-1571260899304-425eee4c7efc?q=80&w=1200&auto=format&fit=crop" alt="Students" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-gold-500 rounded-2xl p-6 hidden md:flex flex-col justify-center text-ink-950 shadow-2xl">
                <div className="font-display text-4xl font-bold">10+</div>
                <div className="text-xs font-semibold uppercase tracking-wider mt-1">Years of Impact</div>
              </div>
              <div className="absolute -top-6 -left-6 w-28 h-28 rounded-2xl bg-brand-800 text-white p-4 grid place-items-center text-center shadow-xl hidden md:grid">
                <div>
                  <div className="font-display text-2xl font-bold">NCCE</div>
                  <div className="text-[10px] uppercase tracking-wider opacity-80">Accredited</div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-700 dark:text-brand-400 mb-3">About the College</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100 mb-5 leading-tight">A Community of Scholars Dedicated to Educational Excellence</h2>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-4">
                {SCHOOL.fullName} is a leading institution in northern Nigeria committed to producing exceptional teachers who shape the next generation. We blend tradition with innovation — combining rigorous academic standards with practical classroom experience.
              </p>
              <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-6">
                Our faculty comprises experienced academics and practitioners, and our facilities — science laboratories, ICT centres, library and demonstration schools — provide students with the resources they need to thrive.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {pillars.map(p => (
                  <div key={p.title} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center shrink-0">
                      <p.icon className="w-5 h-5 text-brand-700 dark:text-brand-400" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-ink-900 dark:text-ink-100">{p.title}</div>
                      <div className="text-xs text-ink-500 dark:text-ink-400 leading-snug">{p.text}</div>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/about">
                <Button className="bg-brand-800 dark:bg-brand-700 hover:bg-brand-900 dark:hover:bg-brand-800">
                  Learn More About Us <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section className="py-20 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <div className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-700 dark:text-brand-400 mb-3">Latest News</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 dark:text-ink-100">News & Events</h2>
            </div>
            <Link to="/news" className="text-sm font-semibold text-brand-700 dark:text-brand-400 inline-flex items-center gap-1 hover:gap-2 transition-all">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {news.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-sm text-ink-500 dark:text-ink-400">No news articles published yet.</CardContent></Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {news.map(n => (
                <Card key={n.id} className="card-lift overflow-hidden border-0 shadow-md">
                  <div className="aspect-[16/10] overflow-hidden bg-ink-100 dark:bg-ink-800">
                    {n.image_url ? (
                      <img src={n.image_url} alt={n.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center bg-gradient-to-br from-brand-100 to-gold-100 dark:from-brand-900/40 dark:to-gold-900/40">
                        <BookOpen className="w-10 h-10 text-brand-400 dark:text-brand-500" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="success">{n.category}</Badge>
                      <span className="text-[11px] text-ink-400">{formatDate(n.published_at)}</span>
                    </div>
                    <h3 className="font-display font-bold text-ink-900 dark:text-ink-100 leading-tight mb-2 line-clamp-2">{n.title}</h3>
                    <p className="text-sm text-ink-600 dark:text-ink-400 line-clamp-2">{n.excerpt}</p>
                    <Link to={`/news/${n.slug}`} className="text-xs font-semibold text-brand-700 dark:text-brand-400 inline-flex items-center gap-1 mt-3">
                      Read more <ArrowRight className="w-3 h-3" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-2xl mb-12">
            <div className="inline-block text-xs font-semibold uppercase tracking-widest text-gold-300 mb-3">Why YMS COE</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Where Tradition Meets Innovation in Teacher Education</h2>
            <p className="text-white/70">We provide a nurturing, intellectually rigorous environment that prepares graduates to become educators of distinction.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map(p => (
              <div key={p.title} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-gold-500/20 grid place-items-center mb-4">
                  <p.icon className="w-6 h-6 text-gold-300" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{p.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-20 bg-ink-50 dark:bg-ink-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Quote className="w-12 h-12 text-gold-400 mx-auto mb-6" />
            <blockquote className="font-display text-2xl md:text-3xl font-medium text-ink-900 dark:text-ink-100 leading-snug mb-6">
              "Education is the most powerful weapon which you can use to change the world. At YMS COE, we forge that weapon with care, character and conviction."
            </blockquote>
            <div className="text-sm text-ink-500 dark:text-ink-400">
              <div className="font-semibold text-ink-900 dark:text-ink-100">— College Motto</div>
              <div>{SCHOOL.motto}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-800 to-brand-950 text-white p-10 md:p-16">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-gold-500/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-brand-400/10 rounded-full blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block text-xs font-semibold uppercase tracking-widest text-gold-300 mb-3">Take the Next Step</div>
                <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Begin Your Journey Toward a Career in Education</h2>
                <p className="text-white/80 mb-6">Applications are now open for the next academic session. Join a community of scholars dedicated to shaping Nigeria's future, one classroom at a time.</p>
                <div className="flex flex-wrap gap-3">
                  <Link to={admissionOpen ? "/portal/register" : "/admission-requirements"}>
                    <Button size="lg" className="bg-gold-500 hover:bg-gold-600 text-ink-950">
                      {admissionOpen ? "Apply Now" : "View Requirements"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/admission-requirements">
                    <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/10">View Requirements</Button>
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: HandHeart, value: "100+", label: "Resources Available" },
                  { icon: Building2, value: "5", label: "Faculties" },
                  { icon: GraduationCap, value: "19", label: "Departments" },
                  { icon: Users, value: "12K+", label: "Alumni Network" },
                ].map(s => (
                  <div key={s.label} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-5">
                    <s.icon className="w-7 h-7 text-gold-300 mb-2" />
                    <div className="font-display font-bold text-xl">{s.value}</div>
                    <div className="text-xs text-white/70">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
